/**
 * ProjectPage — rendered at /app/project/:projectId (nested under
 * ProjectDataLoader, which fetches and hydrates the project before this
 * component ever mounts — see src/hooks/useProjectData.ts).
 *
 * Responsibilities:
 *  - Renders the canvas, side panels, and modals for the active project,
 *    reading project/tree state straight from the (already-hydrated) store.
 *  - Auto-persists OST changes to the backend (debounced).
 *  - Handles node addition flow (pending placement, add-node modal).
 *  - Handles keyboard Delete/Backspace on selected canvas nodes.
 *
 * The URL is the single source of truth for the active project.
 * Zustand stores project data (nodes, positions) but NOT navigation state.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useStore } from '../../store/useStore';
import { saveProjectOst, saveOst } from '../../api/projects';
import { isErrorResponse } from '../../utils/apiUtils';
import { NODE_W, NODE_H, getNodeHeight } from '../../utils/canvasUtils';
import { NodeType, OstNode, NewNodePayload } from '../../types';
import { canAddNodes } from '../../pricing/limits';
import { FREE_NODE_LIMIT } from '../../pricing/pricingTiers';
import Canvas from '../canvas/Canvas';
import NodePanel from '../panels/NodePanel';
import InsightsPanel from '../panels/InsightsPanel';
import TriagePanel from '../panels/TriagePanel';
import AddNodeModal from '../modals/AddNodeModal';
import PageLoadingState from '../ui/PageLoadingState';
import { AppShellContext } from './AppShell';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AddModalState {
    type: NodeType;
    parentId: string | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders the full project canvas with all supporting panels and modals.
 * The active project is determined entirely by the :projectId URL param.
 */
export default function ProjectPage() {
    const { projectId } = useParams<{ projectId: string }>();
    const { showToast, openProjectMenu } = useOutletContext<AppShellContext>();

    const {
        user,
        outcome,
        opportunities,
        solutions,
        userStories,
        positions,
        setPosition,
        nodePanelPosition,
        solutionChildType,
        connections,
        insights,
        insightLinks,
        triageItems,
        addNode,
        activeOstId,
        treeLoading,
        canDeleteNode,
        deleteNode,
    } = useStore();


    // ─── Local UI State ───────────────────────────────────────────────────────

    /** The node whose NodePanel is currently open. */
    const [selectedNode, setSelectedNode] = useState<OstNode | null>(null);

    /** Controls the AddNodeModal for modal-confirmed node creation. */
    const [addModal, setAddModal] = useState<AddModalState | null>(null);

    /**
     * Nodes queued for click-to-place positioning on the canvas.
     * Populated after the add modal is confirmed for non-outcome node types.
     */
    const [pendingNewNodes, setPendingNewNodes] = useState<{
        type: NodeType;
        parentId: string | null;
        data: NewNodePayload;
        alignTop?: boolean;
        /** Set when this pending entry is an existing tree node being repositioned (drag-to-connect
         * — see Canvas.tsx's connectDropTargetNodeId) rather than a brand-new node to create;
         * placePendingNodes below repositions it in place instead of calling addNode. */
        existingNodeId?: string;
    }[]>([]);

    /**
     * True for a brief window right after mount. Project data has already
     * been fetched and hydrated by ProjectDataLoader before this component
     * ever mounts, so the auto-persist effect below would otherwise fire an
     * unnecessary "echo" save of the data that was just loaded. Mirrors the
     * previous fetch-in-progress guard, just scoped to settle-after-mount
     * instead of settle-after-fetch.
     */
    const isMountSettlingRef = useRef(true);
    useEffect(() => {
        const timeout = setTimeout(() => {
            isMountSettlingRef.current = false;
        }, 700);
        return () => clearTimeout(timeout);
    }, []);

    // ─── Clear Pending Nodes on Project Change ────────────────────────────────
    useEffect(() => {
        setPendingNewNodes([]);
    }, [projectId]);

    // ─── Auto-Persist OST Changes ─────────────────────────────────────────────
    // Debounced (600 ms) write to the backend whenever OST state changes.
    // Skipped while the project is still loading to prevent overwriting fresh data.
    const activeProject = useStore((s) => s.activeProject);

    useEffect(() => {
        const persist = async () => {
            if (!user || !activeProject) return;
            if (isMountSettlingRef.current) return;

            const ost = {
                outcome,
                opportunities,
                solutions,
                userStories,
                positions,
                nodePanelPosition,
                solutionChildType,
                connections,
                insights,
                insightLinks,
                triageItems,
            };

            let result;
            if (activeOstId) {
                // @ts-ignore — projects API typings are loose here
                result = await saveOst(activeProject._id, activeOstId, ost, insights);
            } else {
                result = await saveProjectOst(activeProject._id, ost, insights);
            }

            if (isErrorResponse(result)) {
                console.error('Auto-save failed:', result.error);
                showToast(`Save failed: ${result.error}`);
            }
        };

        const timeout = setTimeout(persist, 600);
        return () => clearTimeout(timeout);
    }, [
        user,
        activeProject,
        activeOstId,
        outcome,
        opportunities,
        solutions,
        userStories,
        positions,
        nodePanelPosition,
        solutionChildType,
        connections,
        insights,
        insightLinks,
        triageItems,
        showToast,
    ]);

    // ─── Keyboard Handler (Delete / Backspace) ────────────────────────────────
    useEffect(() => {
        const onKeyDown = async (e: KeyboardEvent) => {
            if (e.key === 'Escape' && pendingNewNodes.length > 0) {
                setPendingNewNodes([]);
                return;
            }

            if (!['Delete', 'Backspace'].includes(e.key)) return;
            if (!selectedNode) return;

            const activeElement = document.activeElement as HTMLElement | null;
            if (
                activeElement &&
                (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) ||
                    activeElement.isContentEditable)
            ) {
                return;
            }

            e.preventDefault();

            if (!canDeleteNode(selectedNode.type, selectedNode.id)) {
                showToast('Cannot delete this node — it still has children. Remove child nodes first.');
                return;
            }

            const { githubError } = await deleteNode(selectedNode.type, selectedNode.id);
            if (githubError) showToast(githubError);
            setSelectedNode(null);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedNode, canDeleteNode, deleteNode, showToast, pendingNewNodes]);

    // ─── Node Selection Handler ───────────────────────────────────────────────

    /** Blurs any focused input then sets the selected node for the NodePanel. */
    const handleSelectNode = useCallback((node: OstNode | null) => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        setSelectedNode(node);
    }, []);

    // ─── Add Node Handlers ────────────────────────────────────────────────────

    /**
     * Nodes currently in the active tree. Shared by every node-creation path
     * (toolbar add, slice panel, AI suggestions) so the free-tier cap is
     * checked against the same count everywhere.
     */
    const totalNodeCount =
        (outcome ? 1 : 0) + opportunities.length + solutions.length + userStories.length;

    /** Validate preconditions then open the add-node modal or directly add. */
    const handleAddNode = async (
        type: NodeType,
        parentId: string | null,
        data: NewNodePayload | null = null,
    ) => {
        if (!activeProject) {
            alert('Create or select a project first. OST nodes must belong to an active project.');
            return;
        }

        if (!activeOstId) {
            if (window.confirm('No tree selected. Would you like to open Project config to create/select a tree?')) {
                openProjectMenu();
            }
            return;
        }

        const isReplacement = type === 'outcome' && outcome;
        if (!isReplacement && !canAddNodes(user, totalNodeCount, 1)) {
            showToast(`Node limit reached. Free accounts can have a maximum of ${FREE_NODE_LIMIT} nodes per project.`);
            return;
        }

        if (data) {
            await addNode(type, data, parentId);
            return;
        }

        setAddModal({ type, parentId });
    };

    /** Called when the user confirms a new node in the AddNodeModal. */
    const confirmAdd = async (data: NewNodePayload) => {
        if (!activeProject || !addModal) {
            setAddModal(null);
            return;
        }

        // Outcome nodes are added immediately — no click-to-place required.
        if (addModal.type === 'outcome') {
            addNode('outcome', data, null);
            setAddModal(null);
            return;
        }

        setPendingNewNodes([{ type: addModal.type, parentId: addModal.parentId, data }]);
        setAddModal(null);
    };

    /** Place all pending nodes at the clicked canvas position. */
    const placePendingNodes = async (basePosition: { x: number; y: number }) => {
        if (pendingNewNodes.length === 0) return;

        const SPACING = NODE_W + 25;
        const anyAlignTop = pendingNewNodes.some((p) => p.alignTop);
        const topEdgeY = anyAlignTop ? basePosition.y - NODE_H / 2 : basePosition.y;

        for (let i = 0; i < pendingNewNodes.length; i += 1) {
            const pending = pendingNewNodes[i];
            let y = basePosition.y;
            if (pending.alignTop) {
                const nodeHeight = getNodeHeight({
                    title: pending.data.title ?? '',
                    type: pending.type,
                } as OstNode);
                y = topEdgeY + nodeHeight / 2;
            }
            const position = { x: basePosition.x + i * SPACING, y };
            if (pending.existingNodeId) {
                // Drag-to-connect reposition: the node already exists, just move it.
                setPosition(pending.existingNodeId, position);
            } else {
                await addNode(pending.type, pending.data, pending.parentId, position);
            }
        }

        setPendingNewNodes([]);
    };

    /**
     * Accept AI-generated or sliced node sets for pending placement.
     *
     * Covers the slice panel (manual and AI-suggested slices) and AI-suggested
     * outcome/opportunity sets — every batch-creation path funnels through
     * here, so the free-tier node cap is enforced once rather than at each
     * caller. Reposition-only entries (drag-to-reparent, carrying an
     * `existingNodeId`) don't create new nodes and are excluded from the count.
     */
    const handlePendingNewNodes = useCallback(
        (nodes: { type: NodeType; parentId: string | null; data: NewNodePayload; alignTop?: boolean; existingNodeId?: string }[]) => {
            if (!nodes || nodes.length === 0) return;

            const newNodeCount = nodes.filter((n) => !n.existingNodeId).length;
            if (newNodeCount > 0 && !canAddNodes(user, totalNodeCount, newNodeCount)) {
                showToast(`Node limit reached. Free accounts can have a maximum of ${FREE_NODE_LIMIT} nodes per project.`);
                return;
            }

            setPendingNewNodes(nodes);
        },
        [user, totalNodeCount, showToast],
    );

    const cancelPendingNodes = useCallback(() => setPendingNewNodes([]), []);

    // ─── Derived State ────────────────────────────────────────────────────────

    const isCurrentProjectEmpty =
        !outcome &&
        opportunities.length === 0 &&
        solutions.length === 0 &&
        userStories.length === 0;

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Canvas Area ───────────────────────────────────────────── */}
            <div className='app-canvaswrap'>
                {treeLoading ? (
                    <PageLoadingState title='Loading tree…' hint='Switching trees — please wait.' />
                ) : isCurrentProjectEmpty ? (
                    <div className='app-empty'>
                        <p className='app-empty-title'>No nodes yet</p>
                        <p className='app-empty-hint'>
                            Start by creating a tree, then add an outcome to build it.
                        </p>
                        <button
                            className='app-cta-btn'
                            onClick={() =>
                                activeOstId ? handleAddNode('outcome', null) : openProjectMenu()
                            }
                        >
                            {activeOstId ? 'Add first Outcome' : 'Create tree first'}
                        </button>
                    </div>
                ) : (
                    <Canvas
                        projectId={activeProject?._id}
                        selectedNode={selectedNode}
                        onSelectNode={handleSelectNode}
                        onAddNode={handleAddNode}
                        pendingNewNodes={pendingNewNodes}
                        onPlacePendingNodes={placePendingNodes}
                        onCancelPendingNodes={cancelPendingNodes}
                        onStartPendingNodes={handlePendingNewNodes}
                    />
                )}
            </div>

            {/* ── Node side panel ───────────────────────────────────────── */}
            {selectedNode && (
                <NodePanel
                    node={selectedNode}
                    projectId={activeProject?._id}
                    ostId={activeOstId || 'legacy'}
                    onClose={() => setSelectedNode(null)}
                    onAdd={handleAddNode}
                    onError={showToast}
                />
            )}

            {/* ── Insights and Triage panels ────────────────────────────── */}
            <InsightsPanel />
            <TriagePanel />

            {/* ── Add-node modal ────────────────────────────────────────── */}
            {addModal && (
                <AddNodeModal
                    type={addModal.type}
                    onConfirm={confirmAdd}
                    onClose={() => setAddModal(null)}
                />
            )}
        </>
    );
}
